import React, { useEffect, useRef } from 'react';
import { Typography } from '@mui/material';
import { select } from 'd3-selection';
import { scaleBand, scaleLinear } from 'd3-scale';
import { min, max } from 'd3-array';
import TravelGraphsHeading from '../TravelGraphsHeading';
import Styles from './AirTempGraph.module.scss';

interface Props {
  data: {
    month?: string;
    min: number;
    minColor?: string;
    max: number;
    maxColor?: string;
    avg: number;
    avgColor?: string;
    range: number;
    rangeColor?: string;
  }[];
  mobile: boolean;
}

const AirTempGraph: React.FC<Props> = ({ data, mobile }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (mobile || !svgRef.current || !data.length) return undefined;

    const renderChart = () => {
      // Clear previous content
      select(svgRef.current).selectAll('*').remove();

      // Get container dimensions
      const container = svgRef.current?.parentElement;
      if (!container) return;

      const containerWidth = container.clientWidth;

      // Set up dimensions
      const margin = { top: 50, right: 0, bottom: 20, left: 0 };
      const width = containerWidth - margin.left - margin.right;
      const height = 150 - margin.top - margin.bottom;

      // Create SVG
      const svg = select(svgRef.current)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Calculate scales
      const xScale = scaleBand()
        .domain(data.map((d) => d.month || ''))
        .range([0, width])
        .padding(0.01);

      const minValue = min(data, (d) => d.min) || 0;
      const maxValue = max(data, (d) => d.max) || 0;

      const yScale = scaleLinear().domain([minValue, maxValue]).range([height, 0]);

      // Create bars for min values (transparent)
      svg
        .selectAll('.min-bar')
        .data(data)
        .enter()
        .append('rect')
        .attr('class', 'min-bar')
        .attr('x', (d) => xScale(d.month || '') || 0)
        .attr('y', (d) => yScale(d.max))
        .attr('width', xScale.bandwidth())
        .attr('height', (d) => yScale(d.min) - yScale(d.max))
        .attr('fill', 'transparent')
        .attr('stroke', 'transparent')
        .attr('stroke-width', 0);

      // Create bars for range values (semi-transparent blue)
      svg
        .selectAll('.range-bar')
        .data(data)
        .enter()
        .append('rect')
        .attr('class', 'range-bar')
        .attr('x', (d) => xScale(d.month || '') || 0)
        .attr('y', (d) => yScale(d.max))
        .attr('width', xScale.bandwidth())
        .attr('height', (d) => yScale(d.min) - yScale(d.max))
        .attr('fill', 'rgba(49, 78, 230, .4)')
        .attr('stroke', 'rgba(49, 78, 230, .4)')
        .attr('stroke-width', 0);

      // Create bars for max values (transparent)
      svg
        .selectAll('.max-bar')
        .data(data)
        .enter()
        .append('rect')
        .attr('class', 'max-bar')
        .attr('x', (d) => xScale(d.month || '') || 0)
        .attr('y', 0)
        .attr('width', xScale.bandwidth())
        .attr('height', (d) => yScale(d.max))
        .attr('fill', 'transparent')
        .attr('stroke', 'transparent')
        .attr('stroke-width', 0);

      // Add labels
      const labelOffset = 15;
      svg
        .selectAll('.max-label')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'max-label')
        .attr('x', (d) => (xScale(d.month || '') || 0) + xScale.bandwidth() / 2)
        .attr('y', (d) => yScale(d.max) - 5)
        .attr('text-anchor', 'middle')
        .attr('fill', 'currentColor')
        .text((d) => `${Math.round(d.max)}°`);

      svg
        .selectAll('.min-label')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'min-label')
        .attr('x', (d) => (xScale(d.month || '') || 0) + xScale.bandwidth() / 2)
        .attr('y', (d) => yScale(d.min) + labelOffset)
        .attr('text-anchor', 'middle')
        .attr('fill', 'currentColor')
        .text((d) => `${Math.round(d.min)}°`);
    };

    // Initial render
    renderChart();

    let resizeObserver: ResizeObserver | undefined;
    if (svgRef.current?.parentElement) {
      resizeObserver = new ResizeObserver(() => {
        renderChart();
      });
      resizeObserver.observe(svgRef.current.parentElement);
    }

    // Cleanup
    return () => {
      resizeObserver?.disconnect();
    };
  }, [data, mobile]);

  return (
    <div className={Styles.airTempGraph}>
      <TravelGraphsHeading heading="Air Temp" />
      {mobile ? (
        <Typography variant="title2">{Math.round(data[0].avg)}°</Typography>
      ) : (
        <svg
          ref={svgRef}
          role="img"
          focusable="false"
          aria-label="Air Temp Graph showing the minimum, and maximum air temperatures for each month."
        />
      )}
    </div>
  );
};

export default AirTempGraph;
